import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState, useEffect } from 'react';
import { AppText, Button, Column, IconButtonWithLabel, Row, Sensor, Tooltip, } from '@streem/ui-react';
import { artifactTimedResponse } from '@streem/sdk-core';
import { useRoomSession } from '../../../../context/room_session';
import { useObservable } from '../../../../hooks/rxjs';
import { map } from 'rxjs/operators';
import { WallItem } from 'streem-sdk-protobuf';
export function GPSbutton({ roundLeftEdgeYn, roundRightEdgeYn }) {
    const roomSession = useRoomSession();
    const [requestTimedOut, setRequestTimedOut] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [gpsResponse] = useObservable(roomSession.geolocation.responses.pipe(map(responses => responses[responses.length - 1])));
    const [showTooltip, setShowTooltip] = useState(false);
    const [isLockedOpen, setIsLockedOpen] = useState(false);
    const requestGeolocation = useCallback(async () => {
        const id = roomSession.geolocation.requestGeolocation();
        try {
            setRequestTimedOut(false);
            setDisabled(true);
            await artifactTimedResponse(id, roomSession.geolocation.responses);
            setDisabled(false);
        }
        catch (error) {
            setRequestTimedOut(true);
            setDisabled(false);
        }
    }, [roomSession.geolocation]);
    const requestFulfilled = gpsResponse?.status === WallItem.Artifact.Status.FULFILLED;
    const requestRejected = gpsResponse?.status === WallItem.Artifact.Status.REJECTED;
    const requestError = gpsResponse?.status === WallItem.Artifact.Status.ERROR;
    useEffect(() => {
        if (gpsResponse?.status) {
            setShowTooltip(true);
        }
    }, [gpsResponse]);
    const Message = () => {
        if (disabled) {
            return _jsx(RequestSent, {});
        }
        else if (requestFulfilled) {
            return _jsx(Location, {});
        }
        else if (requestRejected) {
            return _jsx(PermissionDenied, {});
        }
        else if (requestError) {
            return _jsx(ErrorEncountered, {});
        }
        else if (requestTimedOut) {
            return _jsx(Unfulfilled, {});
        }
        else {
            return _jsx(Request, {});
        }
    };
    const Address = ({ fullAddress }) => {
        const [streetAddress, city, stateZip] = fullAddress.split(', ');
        const [state, zip] = stateZip.split(' ');
        return (_jsxs(Column, { style: { marginBottom: '8px' }, children: [_jsx(AppText, { color: "dark", size: "small", children: streetAddress }), _jsxs(AppText, { color: "dark", size: "small", children: [city, ", ", state, ", ", zip] })] }));
    };
    const Location = () => {
        const gpsPosition = gpsResponse?.gpsPosition;
        if (gpsPosition?.formattedAddress) {
            return (_jsx(GPSResponseMessage, { text: 'Location', "data-testid": "gps-address", children: _jsx(Address, { fullAddress: gpsPosition.formattedAddress }) }));
        }
        else
            return _jsx(Request, {});
    };
    const Unfulfilled = () => {
        return (_jsx(GPSResponseMessage, { text: `Location request was not fulfilled.`, "data-testid": "gps-unfulfilled" }));
    };
    const PermissionDenied = () => (_jsx(GPSResponseMessage, { text: `Your request for the GPS location was denied. You can request again.`, "data-testid": "gps-permission-denied" }));
    const ErrorEncountered = () => (_jsx(GPSResponseMessage, { text: `There was a problem requesting the GPS location. You can request again.`, "data-testid": "gps-error" }));
    const GPSResponseMessage = ({ text, children, ...props }) => {
        return (_jsxs(Column, { style: { maxWidth: '200px' }, "data-testid": props['data-testid'], children: [_jsx(AppText, { color: "dark", size: "small", style: { marginBottom: '8px' }, children: text }), children, _jsx(Request, {})] }));
    };
    const RequestButton = ({ children, ...props }) => {
        return (_jsx(Button, { variant: "tertiary", fontSize: "small", style: { paddingLeft: '15px' }, ...props, children: children }));
    };
    const InitialRequest = () => {
        if (isLockedOpen) {
            return (_jsxs(Column, { children: [_jsx(AppText, { color: "dark", size: "small", children: "Request location?" }), _jsxs(Row, { style: { justifyContent: 'flex-end' }, children: [_jsx(RequestButton, { "data-testid": "sdk-gps-unlock-tooltip", onClick: () => {
                                    setShowTooltip(false);
                                    setIsLockedOpen(false);
                                }, children: "Cancel" }), _jsx(RequestButton, { "data-testid": "sdk-gps-request-geolocation", onClick: requestGeolocation, children: "Request" })] })] }));
        }
        else {
            return (_jsx(AppText, { size: "small", color: "dark", children: "Click to request location." }));
        }
    };
    const Request = () => {
        if (!gpsResponse?.status) {
            return _jsx(InitialRequest, {});
        }
        if (isLockedOpen) {
            return (_jsx(Column, { children: _jsx(Row, { style: { justifyContent: 'flex-end' }, children: _jsx(RequestButton, { "data-testid": "sdk-gps-request", onClick: requestGeolocation, disabled: disabled, children: "Request again" }) }) }));
        }
        return (_jsx(AppText, { size: "small", color: "dark", children: "Click to request location." }));
    };
    const RequestSent = () => {
        return (_jsx(AppText, { size: "small", color: "dark", style: { padding: '8px' }, children: "Location requested." }));
    };
    const handleClose = () => {
        if (isLockedOpen) {
            return;
        }
        else {
            setShowTooltip(false);
        }
    };
    return (_jsx(Sensor, { onClickOutside: () => {
            setIsLockedOpen(false), setShowTooltip(false);
        }, onEscape: () => {
            setIsLockedOpen(false), setShowTooltip(false);
        }, children: _jsx(Tooltip, { message: _jsx(Message, {}), offsetValue: -15, showTooltip: showTooltip, distance: 20, children: _jsx(IconButtonWithLabel, { "data-testid": "sdk-gps-toggle-tooltip-lock", color: "blue50", backgroundColor: "white", hoverBackgroundColor: "grey10", hoverColor: "azure", highlight: "transparent", label: "GPS", title: "Request GPS location", iconName: "GPSIcon", onClick: () => {
                    setIsLockedOpen(!isLockedOpen);
                }, onMouseEnter: () => setShowTooltip(true), onMouseLeave: handleClose, onMouseOver: () => setShowTooltip(true), roundLeftEdgeYn: roundLeftEdgeYn, roundRightEdgeYn: roundRightEdgeYn }) }) }));
}
//# sourceMappingURL=gps_button.js.map